import { useChannelLabel } from '../context/ChannelNumberingContext';
import React, { useEffect, useState } from 'react';
import ClusterWaveformPanel from './ClusterWaveformPanel';
import StackedClusterWaveforms from './StackedClusterWaveforms';
import apiClient from '../api/client';

function primaryChannelFor(cluster, waveforms = []) {
  const declared = cluster?.primaryChannel ?? cluster?.channelId ?? cluster?.channel;
  if (declared != null) return declared;
  const counts = new Map();
  (cluster?.spikeChannels || []).forEach(channel => {
    if (channel != null) counts.set(channel, (counts.get(channel) || 0) + 1);
  });
  if (counts.size) return [...counts].sort((a, b) => b[1] - a[1])[0][0];
  return waveforms[0]?.channel;
}

function ChannelGroup({ channel, primaryChannel, ids, clusterWaveforms, highlightedSpike, initialMode, stacked }) {
  const [reference, setReference] = useState('0');
  let min = Infinity;
  let max = -Infinity;
  ids.forEach(id => (clusterWaveforms[id] || []).forEach(waveform => (waveform.amplitude || []).forEach(value => {
    if (Number.isFinite(value)) { min = Math.min(min, value); max = Math.max(max, value); }
  })));
  const padding = (max - min) * 0.05 || 1;
  const amplitudeRange = Number.isFinite(min) ? [min - padding, max + padding] : undefined;
  const referenceAmplitude = reference.trim() !== '' && Number.isFinite(Number(reference)) ? Number(reference) : null;
  return <section className="cluster-waveform-channel-group" aria-label={channel}>
    <header><strong>{channel}</strong>{' '}
      <label>Horizontal reference (amplitude){' '}
        <input aria-label={`Horizontal reference for ${channel}`} type="number" step="any" value={reference}
          onChange={event => setReference(event.target.value)} />
      </label>
    </header>
    {stacked ? <StackedClusterWaveforms ids={ids} primaryChannel={primaryChannel} clusterWaveforms={clusterWaveforms}
      referenceAmplitude={referenceAmplitude} highlightedSpike={highlightedSpike} /> : <div className="cluster-waveform-columns">
      {ids.map(clusterId => <ClusterWaveformPanel key={clusterId} clusterId={clusterId}
        waveforms={clusterWaveforms[clusterId]} highlightedSpike={highlightedSpike} initialMode={initialMode}
        amplitudeRange={amplitudeRange} referenceAmplitude={referenceAmplitude} />)}
    </div>}
  </section>;
}

export default function ClusterWaveformGroups({ selectedClusters, clusterData, clusterWaveforms, loadNeighbors = false, dataCacheScope = 'default', ...props }) {
  const labelChannel = useChannelLabel();
  const [remote, setRemote] = useState({});
  const requests = selectedClusters.map(id => {
    const cluster = clusterData?.clusters?.find(item => String(item.id ?? item.clusterId) === String(id));
    const channel = primaryChannelFor(cluster, clusterWaveforms[id]);
    if (!loadNeighbors || !props.stacked || channel == null || !cluster?.spikeTimes?.length) return null;
    const count = Math.min(48, cluster.spikeTimes.length);
    return { id, spikes: Array.from({ length: count }, (_, index) => {
      const pointIndex = Math.floor(index * cluster.spikeTimes.length / count);
      return { time: cluster.spikeTimes[pointIndex], channel, pointIndex, spikeIndex: cluster.spikeIndices?.[pointIndex] ?? pointIndex };
    }) };
  }).filter(Boolean);
  const requestKey = JSON.stringify({ scope: dataCacheScope, requests });
  useEffect(() => {
    const controller = new AbortController();
    const { requests: pending } = JSON.parse(requestKey);
    setRemote({ key: requestKey, loading: pending.length > 0, waveforms: {}, error: '' });
    if (!pending.length) return () => controller.abort();
    Promise.all(pending.map(async ({ id, spikes }) => {
      const result = await apiClient.getSpikeWaveformCards({ spikes, neighborRadius: 4, signal: controller.signal });
      return [id, (result.waveforms || []).map(waveform => ({ ...waveform, timeUnit: result.timeUnit || 'samples' }))];
    })).then(entries => {
      if (!controller.signal.aborted) setRemote({ key: requestKey, loading: false, waveforms: Object.fromEntries(entries) });
    }).catch(error => {
      if (!controller.signal.aborted) setRemote({ key: requestKey, loading: false, error: error.message });
    });
    return () => controller.abort();
  }, [requestKey]);
  const available = remote.key === requestKey ? { ...clusterWaveforms, ...remote.waveforms } : clusterWaveforms;
  const groups = new Map();
  selectedClusters.forEach(id => {
    const cluster = clusterData?.clusters?.find(item => String(item.id ?? item.clusterId) === String(id));
    const primary = primaryChannelFor(cluster, clusterWaveforms[id]);
    const channel = primary == null ? 'Primary channel unavailable' : `Primary channel ${labelChannel(primary)}`;
    if (!groups.has(channel)) groups.set(channel, { primary, ids: [] });
    groups.get(channel).ids.push(id);
  });
  return <div className="cluster-waveform-groups">
    {remote.key === requestKey && remote.loading && <p role="status">Loading neighboring-channel waveforms…</p>}
    {remote.key === requestKey && remote.error && <p role="alert">Unable to load neighboring channels: {remote.error}</p>}
    {props.stacked && <p>Recorded channels only · primary channel in red · shared amplitude and time scales · orange line marks time zero</p>}
    {[...groups].map(([channel, { primary, ids }]) => <ChannelGroup key={primary ?? 'unavailable'} channel={channel} primaryChannel={primary} ids={ids}
      clusterWaveforms={available} {...props} />)}
  </div>;
}
